import * as React from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    MenuItem,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { useCanAccess } from '@react-admin/ra-rbac';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import {
    getHelsinkiDate,
    getMergeReportSheet,
    getSchoolReportFilename,
    MergeReport,
    formatMergeReportTime,
    formatReleasedProducts,
    formatReleasedLicenseCount,
    formatMergeMpassStatus,
} from './mergeReport';
import { SchoolMergeContext } from './mergeNavigation';

const MergeReportPage = ({
    resource,
    school,
    onBusyChange,
}: SchoolMergeContext) => {
    const valid =
        ['student', 'pupil', 'teacher'].includes(resource) &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            school
        );
    const { canAccess, isPending } = useCanAccess({ action: 'read', resource });
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();
    const [reportFrom, setReportFrom] = React.useState(getHelsinkiDate);
    const [reportUntil, setReportUntil] = React.useState(getHelsinkiDate);
    const [exporting, setExporting] = React.useState(false);
    const [scope, setScope] = React.useState('latest');
    const validDates =
        scope === 'latest' ||
        Boolean(reportFrom && reportUntil && reportFrom <= reportUntil);
    const query = new URLSearchParams({ school, resource });
    if (scope === 'dates') {
        query.set('from', reportFrom);
        query.set('until', reportUntil);
    }
    const reportQuery = query.toString();
    const [loaded, setLoaded] = React.useState<{
        query: string;
        report: MergeReport | null;
        error: boolean;
    } | null>(null);
    const current = loaded?.query === reportQuery ? loaded : null;
    const report = current?.report;
    React.useEffect(() => {
        if (!valid || !canAccess || !validDates) return;
        let cancelled = false;
        setLoaded(null);
        dataProvider
            .getAny('user/merge-report', reportQuery)
            .then(({ data }: { data: MergeReport }) => {
                if (!cancelled)
                    setLoaded({
                        query: reportQuery,
                        report: data,
                        error: false,
                    });
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                console.error(err);
                setLoaded({ query: reportQuery, report: null, error: true });
                notify('merge.history.report_error', { type: 'error' });
            });
        return () => {
            cancelled = true;
        };
    }, [valid, canAccess, validDates, reportQuery, dataProvider, notify]);

    const exportReport = async () => {
        if (!valid || !canAccess || exporting) return;
        if (
            scope === 'dates' &&
            (!reportFrom || !reportUntil || reportFrom > reportUntil)
        ) {
            notify('merge.history.report_invalid_dates', { type: 'warning' });
            return;
        }
        if (!report?.data.length) return;
        setExporting(true);
        try {
            const data = report;
            const from =
                scope === 'latest'
                    ? getHelsinkiDate(
                          new Date(data.data[data.data.length - 1].created)
                      )
                    : reportFrom;
            const until =
                scope === 'latest'
                    ? getHelsinkiDate(new Date(data.data[0].created))
                    : reportUntil;
            const { default: writeExcelFile } =
                await import('write-excel-file/browser');
            await writeExcelFile(
                getMergeReportSheet(
                    data,
                    from,
                    until,
                    translate(`resources.${resource}.name`, { smart_count: 2 }),
                    translate
                ),
                {
                    sheet: 'Yhdistamiset',
                    columns: [
                        { width: 25 },
                        { width: 32 },
                        { width: 38 },
                        { width: 32 },
                        { width: 38 },
                        { width: 38 },
                        { width: 24 },
                        { width: 60 },
                        { width: 22 },
                    ],
                }
            ).toFile(
                getSchoolReportFilename(
                    'yhdistamiset',
                    data.school_name,
                    resource,
                    `${scope === 'latest' ? 'viimeisin-era-' : ''}${from}-${until}`
                )
            );
        } catch (err) {
            console.error(err);
            notify('merge.history.report_error', { type: 'error' });
        } finally {
            setExporting(false);
        }
    };

    React.useEffect(() => {
        onBusyChange?.(exporting);
        return () => onBusyChange?.(false);
    }, [exporting, onBusyChange]);

    if (!valid)
        return (
            <Alert severity="error">
                {translate('merge.bulk.invalid_context')}
            </Alert>
        );
    if (isPending) return <CircularProgress />;
    if (!canAccess)
        return (
            <Alert severity="warning">
                {translate('merge.bulk.no_permission')}
            </Alert>
        );
    return (
        <Box p={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
                <Typography variant="h5">
                    {translate('merge.history.report_title')}
                </Typography>
            </Stack>
            <Paper sx={{ p: 2 }}>
                {report?.school_name && (
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {report.school_name}
                    </Typography>
                )}
                {report?.data.some(
                    entry =>
                        entry.released_school_licenses == null ||
                        entry.released_products == null
                ) && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {translate('merge.history.report_missing_audit')}
                    </Alert>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        select
                        size="small"
                        label={translate('merge.history.report_scope')}
                        value={scope}
                        onChange={event => setScope(event.target.value)}
                        disabled={exporting}
                        sx={{ minWidth: 240 }}
                    >
                        <MenuItem value="latest">
                            {translate('merge.history.report_latest')}
                        </MenuItem>
                        <MenuItem value="dates">
                            {translate('merge.history.report_dates')}
                        </MenuItem>
                    </TextField>
                    {scope === 'dates' && (
                        <>
                            <TextField
                                type="date"
                                size="small"
                                required
                                label={translate('merge.history.report_from')}
                                value={reportFrom}
                                onChange={event =>
                                    setReportFrom(event.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ max: reportUntil || undefined }}
                                disabled={exporting}
                            />
                            <TextField
                                type="date"
                                size="small"
                                required
                                label={translate('merge.history.report_until')}
                                value={reportUntil}
                                onChange={event =>
                                    setReportUntil(event.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: reportFrom || undefined }}
                                disabled={exporting}
                            />
                        </>
                    )}
                    <Button
                        sx={{ px: 3, flexShrink: 0, whiteSpace: 'nowrap' }}
                        startIcon={
                            exporting ? (
                                <CircularProgress size={18} />
                            ) : (
                                <DownloadIcon />
                            )
                        }
                        onClick={exportReport}
                        disabled={
                            exporting ||
                            !report?.data.length ||
                            (scope === 'dates' &&
                                (!reportFrom ||
                                    !reportUntil ||
                                    reportFrom > reportUntil))
                        }
                    >
                        {translate('merge.history.report_export')}
                    </Button>
                </Stack>
                {!validDates ? (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {translate('merge.history.report_invalid_dates')}
                    </Alert>
                ) : current?.error ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {translate('merge.history.report_error')}
                    </Alert>
                ) : !report ? (
                    <CircularProgress sx={{ mt: 2 }} />
                ) : !report.data.length ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        {translate('merge.history.report_empty')}
                    </Alert>
                ) : (
                    <TableContainer sx={{ mt: 2 }}>
                        <Table
                            size="small"
                            aria-label={translate('merge.history.report_title')}
                        >
                            <TableHead>
                                <TableRow>
                                    {[
                                        'created',
                                        'report_primary_name',
                                        'report_primary_identifier',
                                        'report_source_name',
                                        'report_source_identifier',
                                        'report_identifier_after',
                                        'report_released_licenses',
                                        'report_released_products',
                                        'mpass_status',
                                    ].map(key => (
                                        <TableCell key={key}>
                                            {translate(`merge.history.${key}`)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.data.map((entry, index) => (
                                    <TableRow key={index}>
                                        <TableCell
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            {formatMergeReportTime(
                                                entry.created
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {entry.primary.name}
                                        </TableCell>
                                        <TableCell>
                                            {entry.primary.identifier}
                                        </TableCell>
                                        <TableCell>
                                            {entry.source.name}
                                        </TableCell>
                                        <TableCell>
                                            {entry.source.identifier}
                                        </TableCell>
                                        <TableCell>
                                            {entry.identifier_after}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatReleasedLicenseCount(
                                                entry,
                                                translate
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {entry.released_products?.length ? (
                                                <Box component="details">
                                                    <Box
                                                        component="summary"
                                                        sx={{
                                                            cursor: 'pointer',
                                                            color: 'primary.main',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {translate(
                                                            'merge.history.report_show_products'
                                                        )}
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            mt: 1,
                                                            maxHeight: 200,
                                                            width: 280,
                                                            maxWidth: '100%',
                                                            overflowY: 'auto',
                                                            overflowWrap: 'anywhere',
                                                            whiteSpace: 'pre-line',
                                                        }}
                                                    >
                                                        {formatReleasedProducts(
                                                            entry.released_products,
                                                            translate
                                                        )}
                                                    </Box>
                                                </Box>
                                            ) : (
                                                formatReleasedProducts(
                                                    entry.released_products,
                                                    translate
                                                )
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {formatMergeMpassStatus(
                                                entry.mpass_status,
                                                translate
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default MergeReportPage;
